import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { FaChevronDown, FaUsers } from 'react-icons/fa';
import { useFetchWrapper } from '../../_helpers/fetchWrapper.js';
import { isSignedIn } from '../../_helpers/safeRedirect.js';
import { inlineStartOffset } from '../../_helpers/rtl.js';
import { useLocale } from '../../contexts/LocaleContext.jsx';
import config from '../../config.js';
import '../../styles/partial/BandProfileDropdown.css';

/** Below this width the dropdown is centred instead of anchored to the button. */
const CENTER_BREAKPOINT = 588;

const BandProfileDropdown = () => {
  const navigate = useNavigate();
  const { t, dir } = useLocale();
  const [myBands, setMyBands] = useState([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [buttonRect, setButtonRect] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const fetchWrapperRef = useRef(useFetchWrapper());
  const buttonRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  // Whether the menu is open because the pointer came over the button, and
  // the button has not been clicked since (ENG-561). A mouse user hovers and
  // then clicks, so that click must not shut what the hover opened; it
  // claims the menu instead, and the next click (or a keyboard press) closes.
  const openedByHoverRef = useRef(false);

  useEffect(() => {
    const fetchMyBands = async () => {
      // A signed-out visitor has no bands, and asking answers 401 (ENG-555).
      if (!isSignedIn()) return;
      try {
        const data = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/user/mybands`);
        setMyBands(data || []);
      } catch (error) {
        console.error('Error fetching bands:', error);
        setMyBands([]);
      }
    };

    fetchMyBands();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target)) {
        // Check if click is inside dropdown
        const dropdownElement = document.querySelector('.band-dropdown');
        if (!dropdownElement || !dropdownElement.contains(event.target)) {
          openedByHoverRef.current = false;
          setIsDropdownVisible(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Clear any pending timeout when component unmounts
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Window resize listener for responsive positioning
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const hideDropdown = () => {
    openedByHoverRef.current = false;
    setIsDropdownVisible(false);
  };

  const handleBandSelect = (selectedBandId) => {
    hideDropdown();
    navigate(`/performers/${selectedBandId}`);
  };

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (myBands.length > 1) {
      // Calculate button position for dropdown positioning
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setButtonRect(rect);
      }
      if (!isDropdownVisible) openedByHoverRef.current = true;
      setIsDropdownVisible(true);
    }
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(hideDropdown, 200); // 200ms delay
  };

  const handleDropdownMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleDropdownMouseLeave = () => {
    hideDropdown();
  };

  const handleButtonClick = () => {
    if (myBands.length > 1) {
      if (isDropdownVisible && openedByHoverRef.current) {
        // The hover opened it a moment ago; this click keeps it (ENG-561).
        openedByHoverRef.current = false;
      } else if (isDropdownVisible) {
        hideDropdown();
      } else {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          setButtonRect(rect);
        }
        openedByHoverRef.current = false;
        setIsDropdownVisible(true);
      }
    } else {
      // With one band, Band Profile is that band (ENG-513); it used to stay on
      // whichever band's page was open.
      navigate(`/performers/${myBands[0]?.ID}`);
    }
  };

  // Don't render anything if user is not part of any bands
  if (!myBands || myBands.length === 0) {
    return null;
  }

  return (
    <>
      <button 
        onClick={handleButtonClick}
        className="top-page-nav-button band-profile-button"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={buttonRef}
        aria-expanded={myBands.length > 1 ? isDropdownVisible : undefined}
      >
        {t('navigation.bandProfile')}
        {myBands.length > 1 && <FaChevronDown className="dropdown-arrow" aria-hidden="true" />}
      </button>
      
      {isDropdownVisible && myBands.length > 1 && buttonRect && createPortal(
        <div 
          className={`band-dropdown${windowWidth <= CENTER_BREAKPOINT ? ' band-dropdown--centered' : ''}`}
          onMouseEnter={handleDropdownMouseEnter}
          onMouseLeave={handleDropdownMouseLeave}
          // Placement goes through custom properties consumed by logical
          // `top` / `inset-inline-start` in BandProfileDropdown.css, so the
          // menu stays under its button when the layout is mirrored for RTL.
          style={{
            '--band-dropdown-top': `${buttonRect.bottom + 5}px`,
            '--band-dropdown-inline-start': `${inlineStartOffset(dir, buttonRect, windowWidth)}px`,
          }}
        >
          {myBands.map(band => (
            <div 
              key={band.ID} 
              className="band-dropdown-item"
              onClick={() => handleBandSelect(band.ID)}
            >
              {band.ProfileImageURL ? (
                <img
                  src={band.ProfileImageURL}
                  alt={t('navigation.bandProfileImageAlt', { name: band.Name })}
                  className="band-dropdown-image"
                />
              ) : (
                <FaUsers className="band-dropdown-image" />
              )}
              <span className="band-dropdown-name">{band.Name}</span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export default BandProfileDropdown;
